import { ImageUp, SwitchCamera, X, ZoomIn } from "lucide-react";
import { useEffect, useRef, useState, type ChangeEvent, type TouchEvent } from "react";
import {
  applyNativeZoom,
  blobToFile,
  cameraSupported,
  captureFrameToBlob,
  describeCameraError,
  getCameraStream,
  isIosStandalone,
  probeZoomCapability,
  stopStream,
  type Facing,
  type ZoomCapability
} from "../../lib/cameraCapture";

type Props = {
  onCaptured: (file: File) => void;
  onClose: () => void;
};

function touchDistance(touches: TouchEvent["touches"]): number {
  const [a, b] = [touches[0], touches[1]];
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

// Full-screen Locket-style camera. Live getUserMedia when available; automatic fallback to the
// native camera (<input capture>) on iOS standalone / unsupported / permission-denied.
export function MomentsCameraView({ onCaptured, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pinchRef = useRef<{ startDist: number; startZoom: number } | null>(null);

  const [facing, setFacing] = useState<Facing>("environment");
  const [useFallback, setUseFallback] = useState(() => !cameraSupported() || isIosStandalone());
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [zoomCap, setZoomCap] = useState<ZoomCapability | null>(null);
  const [zoom, setZoom] = useState(1);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (useFallback) {
      return;
    }
    let cancelled = false;
    setStarting(true);
    setError(null);
    (async () => {
      try {
        stopStream(streamRef.current);
        const stream = await getCameraStream(facing);
        if (cancelled) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          await videoRef.current.play().catch(() => undefined);
        }
        const cap = probeZoomCapability(stream);
        setZoomCap(cap);
        setZoom(cap ? cap.min : 1);
      } catch (err) {
        if (!cancelled) {
          setError(describeCameraError(err));
        }
      } finally {
        if (!cancelled) {
          setStarting(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facing, useFallback]);

  useEffect(() => () => stopStream(streamRef.current), []);

  // Match the browser chrome to the dark camera surface while it's open.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const previous = meta?.getAttribute("content") ?? null;
    meta?.setAttribute("content", "#0B0B0F");
    return () => {
      if (meta && previous !== null) {
        meta.setAttribute("content", previous);
      }
    };
  }, []);

  const minZoom = zoomCap ? zoomCap.min : 1;
  const maxZoom = zoomCap ? zoomCap.max : 4;
  const stepZoom = zoomCap ? zoomCap.step || 0.1 : 0.1;
  const mirror = facing === "user";
  const cssZoom = zoomCap ? 1 : zoom; // native zoom already scales the stream

  const changeZoom = (value: number) => {
    const clamped = Math.min(maxZoom, Math.max(minZoom, value));
    setZoom(clamped);
    if (zoomCap) {
      void applyNativeZoom(streamRef.current, clamped);
    }
  };

  const handleCapture = async () => {
    if (!videoRef.current || capturing) {
      return;
    }
    setCapturing(true);
    try {
      const blob = await captureFrameToBlob(videoRef.current, { cssZoom, mirror });
      onCaptured(blobToFile(blob));
    } catch {
      setError("Không chụp được ảnh, thử lại nhé.");
    } finally {
      setCapturing(false);
    }
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onCaptured(file);
    }
    event.target.value = "";
  };

  const onTouchStart = (event: TouchEvent) => {
    if (event.touches.length === 2) {
      pinchRef.current = { startDist: touchDistance(event.touches), startZoom: zoom };
    }
  };
  const onTouchMove = (event: TouchEvent) => {
    if (event.touches.length === 2 && pinchRef.current) {
      const ratio = touchDistance(event.touches) / pinchRef.current.startDist;
      changeZoom(pinchRef.current.startZoom * ratio);
    }
  };
  const onTouchEnd = () => {
    pinchRef.current = null;
  };

  const nativeCameraInput = (
    <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-[#0B0B0F] text-white"
      style={{ height: "100dvh" }}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4"
        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))", paddingBottom: "0.5rem" }}
      >
        <button
          type="button"
          aria-label="Đóng camera"
          onClick={onClose}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20"
        >
          <X size={22} aria-hidden="true" />
        </button>
        <p className="text-sm font-semibold text-white/80">Khoảnh khắc</p>
        {!useFallback ? (
          <button
            type="button"
            aria-label="Đổi camera trước/sau"
            onClick={() => setFacing((prev) => (prev === "environment" ? "user" : "environment"))}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20"
          >
            <SwitchCamera size={20} aria-hidden="true" />
          </button>
        ) : (
          <span className="h-11 w-11" />
        )}
      </div>

      {/* Viewfinder */}
      <div
        className="relative min-h-0 flex-1 overflow-hidden bg-black"
        style={{ touchAction: "none" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {!useFallback && !error ? (
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="h-full w-full object-cover"
            style={{ transform: `scale(${mirror ? -cssZoom : cssZoom}, ${cssZoom})` }}
          />
        ) : null}

        {starting && !error ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70">Đang mở camera…</div>
        ) : null}

        {(useFallback || error) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <ImageUp size={40} className="text-white/70" aria-hidden="true" />
            <p className="max-w-xs text-sm text-white/80">
              {error ?? "Máy này dùng camera hệ thống để chụp. Nhấn nút bên dưới để mở camera."}
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full bg-white px-6 py-3 text-sm font-bold text-[#0B0B0F] hover:bg-white/90"
            >
              Mở camera hệ thống
            </button>
            {error && cameraSupported() && !isIosStandalone() ? (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setUseFallback(false);
                  setFacing("environment");
                }}
                className="text-xs font-semibold text-white/60 underline"
              >
                Thử lại camera trong app
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div
        className="flex flex-col items-center gap-4 px-6"
        style={{ paddingTop: "1rem", paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
      >
        {!useFallback && !error ? (
          <div className="flex w-full max-w-xs items-center gap-3 text-white/80">
            <ZoomIn size={16} aria-hidden="true" />
            <input
              type="range"
              min={minZoom}
              max={maxZoom}
              step={stepZoom}
              value={zoom}
              onChange={(event) => changeZoom(Number(event.target.value))}
              className="h-1 flex-1 cursor-pointer accent-[#C47A5A]"
              aria-label="Thu phóng"
            />
            <span className="w-9 text-right text-xs tabular-nums">{zoom.toFixed(1)}×</span>
          </div>
        ) : null}

        <div className="flex items-center gap-8">
          {!useFallback && !error ? (
            <>
              <button
                type="button"
                aria-label="Dùng camera hệ thống"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white/80 backdrop-blur hover:bg-white/20"
              >
                <ImageUp size={20} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Chụp ảnh"
                disabled={capturing || starting}
                onClick={handleCapture}
                className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-white/10 ring-4 ring-[#C47A5A] transition active:scale-95 disabled:opacity-60"
              >
                <span className="h-14 w-14 rounded-full bg-white" />
              </button>
              <span className="h-12 w-12" />
            </>
          ) : null}
        </div>
      </div>

      {nativeCameraInput}
    </div>
  );
}
